import React, { useEffect, useState } from 'react';
import { ListeningSet, ListeningQuestion, api } from '../../../config/api';
import { exportListeningTxt } from '../../../utils/listeningExport';

interface Props {
  listeningSet: ListeningSet;
  // Object URL for the locally-uploaded file (the server stores no audio).
  audioUrl: string;
  audioName: string;
  onSubmitScore: (score: any, results: any[]) => void;
  onBack: () => void;
}

const ListeningPractice: React.FC<Props> = ({ listeningSet, audioUrl, audioName, onSubmitScore, onBack }) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A retake from the results page lands here while scrolled to the bottom —
  // start every (re)attempt at question 1.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (q: ListeningQuestion, value: string) => {
    setAnswers((prev) => ({ ...prev, [q.id]: value }));
  };

  const handleExport = () => {
    exportListeningTxt({
      audioName,
      transcript: listeningSet.transcript,
      questions: listeningSet.questions,
      userAnswers: answers,
    });
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      setSubmitting(true);
      const response = await api.submitListeningAnswers(
        listeningSet.id,
        answers,
        listeningSet.questions
      );
      if (response.data) {
        onSubmitScore(response.data.score, response.data.results);
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Could not submit your answers. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="listening-practice">
      <h3 className="section-title">3. Listen and answer</h3>
      {audioUrl && (
        <audio controls src={audioUrl} style={{ width: '100%', marginBottom: '24px' }} />
      )}
      <form className="question-form">
        {listeningSet.questions.map((q, idx) => (
          <div key={q.id} className="question-block" style={{ marginBottom: '20px' }}>
            <p style={{ marginBottom: '8px' }}><strong>{idx + 1}.</strong> {q.question}</p>
            {(() => {
              const options =
                q.options && q.options.length > 0
                  ? q.options
                  : q.type === 'tf'
                  ? ['True', 'False']
                  : [];
              if (q.type === 'mcq' || q.type === 'tf') {
                return options.map((opt) => (
                  <label key={opt} className="option-label" style={{ display: 'block', marginBottom: '6px' }}>
                    <input
                      type="radio"
                      name={q.id}
                      value={opt}
                      checked={answers[q.id] === opt}
                      onChange={(e) => handleChange(q, e.target.value)}
                    />{' '}
                    {opt}
                  </label>
                ));
              }
              // Short answer
              return (
                <input
                  className="form-input"
                  type="text"
                  value={answers[q.id] || ''}
                  onChange={(e) => handleChange(q, e.target.value)}
                />
              );
            })()}
          </div>
        ))}
      </form>
      {error && <p className="error-message">{error}</p>}
      <div className="practice-actions" style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-secondary" onClick={handleExport}>
            Export TXT
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Answers'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListeningPractice;
